/**
 * ContributedItemCard.tsx
 *
 * Renders a card for a contributed item (Recipe or Ingredient).
 * - Recipes are clickable if published, navigating to their detail page.
 * - Unpublished recipes can trigger an update modal.
 * - Ingredients are displayed as static cards.
 *
 * Props:
 * - item: Recipe | Ingredient - The contributed item to display.
 * - onOpenUpdateModal?: () => void - Optional callback for opening update modal (for unpublished recipes).
 */

import React from "react";
import { Card } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Recipe, Ingredient } from "../../types";
import { getStaticAssetPath } from "../../utils/staticAssets";
import { useNavigate } from "react-router-dom";
import ContributedItemCardContent from "./ContributedItemCardContent";
import { ROUTES } from "../../constants/routes";

interface Props {
  item: Recipe | Ingredient;
  onOpenUpdateModal?: () => void;
}

/**
 * Type guard to check if item is a Recipe.
 */
const isRecipe = (item: Recipe | Ingredient): item is Recipe =>
  "status_name" in item && "data" in item;

/**
 * Get the name of the item.
 */
const getName = (item: Recipe | Ingredient) =>
  isRecipe(item) ? item.data.name : item.name;

/**
 * Get the status of the item.
 */
const getStatus = (item: Recipe | Ingredient) =>
  isRecipe(item) ? item.status_name : "Ingredient";

/**
 * Get the image path for the item.
 * For recipes, tries coverImage first, then gallery.
 * Returns undefined if no image found.
 */
const getImagePath = (item: Recipe | Ingredient) => {
  if (isRecipe(item)) {
    if (item.data.coverImage && item.data.coverImage.source) {
      return getStaticAssetPath(item.data.coverImage.source);
    }
    if (
      Array.isArray(item.data.gallery) &&
      item.data.gallery.length > 0 &&
      item.data.gallery[0].source
    ) {
      return getStaticAssetPath(item.data.gallery[0].source);
    }
  }
  return undefined;
};

/**
 * Get the created date of the item as a formatted string.
 */
const getCreatedDate = (item: Recipe | Ingredient) =>
  isRecipe(item)
    ? new Date(item.created_at).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

/**
 * ContributedItemCard component.
 */
const ContributedItemCard: React.FC<Props> = ({ item, onOpenUpdateModal }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Link to recipe detail page if item is a recipe
  const recipeLink = isRecipe(item)
    ? `${ROUTES.RECIPE_DETAIL}/${item._id}`
    : undefined;

  /**
   * Handles click on the card:
   * - Navigates to recipe page if published.
   * - Opens update modal if unpublished and callback provided.
   */
  const handleClick = () => {
    if (isRecipe(item)) {
      if (item.status_name === "Published") {
        navigate(recipeLink!);
      } else if (onOpenUpdateModal) {
        onOpenUpdateModal();
      }
    }
  };

  // Only recipes get click behaviour and focusable role
  return isRecipe(item) ? (
    <Card
      sx={{
        maxWidth: 200,
        borderRadius: 1,
        boxShadow: "none",
        bgcolor: theme.palette.grey[100],
        cursor: "default",
        textDecoration: "none",
        border: "2px solid transparent",
        transition: "border-color 0.2s",
        "&:hover": {
          borderColor: theme.palette.materiom.seaweed,
          boxShadow: "none",
        },
      }}
      onClick={handleClick}
      tabIndex={0}
      role="button"
    >
      {/* Card content for Recipe */}
      <ContributedItemCardContent item={item} />
    </Card>
  ) : (
    <Card
      sx={{
        maxWidth: 200,
        borderRadius: 1,
        boxShadow: "none",
        bgcolor: theme.palette.grey[100],
        cursor: "default",
        border: "2px solid transparent",
        "&:hover": {
          cursor: "default",
        },
      }}
    >
      {/* Card content for Ingredient */}
      <ContributedItemCardContent item={item} />
    </Card>
  );
};

export default ContributedItemCard;
